/**
 * Global Error Interceptor
 * Centralized error handling for all API calls
 */

/**
 * Setup error interceptor for an axios instance
 * @param {Object} axiosInstance - Axios instance to attach interceptor
 */
export const setupErrorInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Handle different error scenarios
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;

        switch (status) {
          case 400:
            console.error("Bad Request:", data.message || "Invalid request");
            break;
          case 401:
            console.error(
              "Unauthorized:",
              data.message || "Authentication required"
            );
            // Could trigger logout or redirect to login
            break;
          case 403:
            console.error("Forbidden:", data.message || "Access denied");
            break;
          case 404:
            console.error("Not Found:", data.message || "Resource not found");
            break;
          case 422:
            console.error(
              "Validation Error:",
              data.message || "Validation failed"
            );
            break;
          case 500:
            console.error(
              "Server Error:",
              data.message || "Internal server error"
            );
            break;
          case 503:
            console.error(
              "Service Unavailable:",
              data.message || "Service temporarily unavailable"
            );
            break;
          default:
            console.error(
              `Error ${status}:`,
              data.message || "An error occurred"
            );
        }
      } else if (error.request) {
        // Request made but no response received
        console.error("Network Error:", "No response from server");
      } else {
        // Something else happened
        console.error("Error:", error.message);
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Setup request interceptor for authentication
 * @param {Object} axiosInstance - Axios instance to attach interceptor
 * @param {Object} store - Vuex store instance
 */
export const setupAuthInterceptor = (axiosInstance, store) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      // Add timestamp for authenticated requests
      const token = store?.getters?.token;
      if (token) {
        const timestamp = new Date().getTime();
        config.headers["wsc-timestamp"] = timestamp;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

export default {
  setupErrorInterceptor,
  setupAuthInterceptor,
};
